import { useMemo } from 'react';
import type { Options } from 'highcharts';
import { useTheme } from '../providers/ThemeProvider';
import { getThemedChartConfig, mergeChartConfig } from '../styles/charts';

/**
 * Hook for creating theme-aware Highcharts configurations
 * 
 * This hook automatically applies the current theme to chart configurations,
 * ensuring consistent styling across light and dark modes.
 */
export const useThemedCharts = () => {
  const { currentTheme } = useTheme();

  // Memoize the themed base configuration to avoid recreating on every render
  const themedBaseConfig = useMemo(() => {
    return getThemedChartConfig(currentTheme);
  }, [currentTheme]);

  /**
   * Get a complete chart configuration with theme applied
   * @param customOptions - Custom Highcharts options to merge with themed base
   * @returns Complete Highcharts configuration with theme applied
   */
  const getChartConfig = useMemo(() => {
    return (customOptions: Partial<Options> = {}): Options => {
      return mergeChartConfig(themedBaseConfig, customOptions) as Options;
    };
  }, [themedBaseConfig]);

  /**
   * Get theme-aware colors for chart series
   * @param count - Number of colors needed
   * @returns Array of theme-appropriate colors
   */
  const getSeriesColors = useMemo(() => {
    return (count: number = 10): string[] => {
      const baseColors = currentTheme.chart.primary;
      const colors: string[] = [];
      
      for (let i = 0; i < count; i++) {
        colors.push(baseColors[i % baseColors.length]);
      }
      
      return colors;
    };
  }, [currentTheme.chart.primary]);

  /**
   * Get theme-aware color for a specific series index
   * @param index - Series index
   * @returns Theme-appropriate color
   */
  const getSeriesColor = useMemo(() => {
    return (index: number): string => {
      const colors = currentTheme.chart.primary;
      return colors[index % colors.length];
    };
  }, [currentTheme.chart.primary]);

  /**
   * Get status colors for different chart contexts
   */
  const getStatusColors = useMemo(() => {
    return {
      success: currentTheme.colors.status.success,
      warning: currentTheme.colors.status.warning,
      error: currentTheme.colors.status.error,
      info: currentTheme.colors.status.info,
      plotLine: currentTheme.colors.text.secondary,
      yAxisTitle: currentTheme.colors.text.primary,
      yAxisLabels: currentTheme.colors.text.secondary,
    };
  }, [currentTheme.colors.status, currentTheme.colors.text]);

  /**
   * Get performance-specific color palettes
   */
  const getPerformanceColors = useMemo(() => {
    return {
      // Efficiency colors (green to red gradient)
      efficiency: currentTheme.isDark 
        ? ['#10b981', '#34d399', '#fbbf24', '#f87171', '#ef4444']
        : ['#059669', '#10b981', '#f59e0b', '#dc2626', '#b91c1c'],
      
      // Output colors (blue gradient)
      output: currentTheme.isDark
        ? ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe', '#dbeafe']
        : ['#1d4ed8', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd'],
      
      // Downtime colors (red gradient)
      downtime: currentTheme.isDark
        ? ['#ef4444', '#f87171', '#fca5a5', '#fecaca', '#fee2e2']
        : ['#b91c1c', '#dc2626', '#ef4444', '#f87171', '#fca5a5'],
      
      // Quality colors (mixed for different quality metrics)
      quality: currentTheme.isDark
        ? ['#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe']
        : ['#6d28d9', '#7c3aed', '#8b5cf6', '#a78bfa', '#c4b5fd'],
    };
  }, [currentTheme.isDark]);

  /**
   * Common chart configurations for different chart types
   */
  const getChartTypeConfig = useMemo(() => {
    return {
      line: {
        chart: { type: 'line' },
        plotOptions: {
          line: {
            lineWidth: 2.5,
            marker: {
              radius: 4,
              symbol: 'circle',
            },
          },
        },
      } as Partial<Options>,

      column: {
        chart: { type: 'column' },
        plotOptions: {
          column: {
            borderRadius: currentTheme.isDark ? 3 : 2,
            borderWidth: 0,
          },
        },
      } as Partial<Options>,

      area: {
        chart: { type: 'area' },
        plotOptions: {
          area: {
            fillOpacity: currentTheme.isDark ? 0.4 : 0.3,
            lineWidth: 2,
          },
        },
      } as Partial<Options>,

      pie: {
        chart: { type: 'pie' },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
              enabled: true,
              format: '<b>{point.name}</b>: {point.percentage:.1f} %',
            },
            showInLegend: true,
          },
        },
      } as Partial<Options>,

      stock: {
        chart: { type: 'line' },
        navigator: {
          enabled: true,
          height: 60,
        },
        rangeSelector: {
          enabled: true,
          selected: 1,
        },
        scrollbar: {
          enabled: true,
        },
      } as Partial<Options>,
    };
  }, [currentTheme.isDark]);

  return {
    // Configuration functions
    getChartConfig,
    getChartTypeConfig,
    
    // Color functions
    getSeriesColors,
    getSeriesColor,
    getStatusColors,
    getPerformanceColors,
    
    // Theme info
    isDark: currentTheme.isDark,
    theme: currentTheme,
  };
};

export default useThemedCharts;
